'use client';

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { Check, Copy, Pencil, Share2, X } from 'lucide-react';

import { useT } from '@/hooks';
import { useUpdateOwnReferralCode } from '@/services/query/referral.queries';

import { MESSAGES } from '@/constants/messages.constants';

interface ReferralLinkCardProps {
  code: string;
  isAmbassador?: boolean;
}

// Mirror the backend rules (referral-helper.js): 4-20 chars, uppercase letters,
// digits, hyphen and underscore. Client-side check for instant feedback; the
// server is the authority (uniqueness + final validation).
const CODE_REGEX = /^[A-Z0-9_-]{4,20}$/;

/**
 * ReferralLinkCard Component
 * Displays the user's unique referral link with a copy-to-clipboard action using MUI.
 */
export const ReferralLinkCard: React.FC<ReferralLinkCardProps> = ({ code, isAmbassador }) => {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const referralUrl = `https://porvenix.com/r/${code}`;

  // --- Self-service code editing ---
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const updateCode = useUpdateOwnReferralCode();

  const draftError =
    draft.length > 0 && !CODE_REGEX.test(draft)
      ? '4–20 characters: A–Z, 0–9, hyphen or underscore'
      : '';
  const canSave = CODE_REGEX.test(draft) && draft !== code && !updateCode.isPending;

  const startEdit = () => {
    setDraft(code);
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
  };
  const saveEdit = () => {
    if (!canSave) return;
    updateCode.mutate(draft, {
      onSuccess: () => {
        setEditing(false);
        setDraft('');
      },
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success(MESSAGES.REFERRAL.LINK_COPIED);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(MESSAGES.REFERRAL.COPY_FAILED);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Porvenix',
          text: 'Trade on Polymarket with Porvenix using my referral link!',
          url: referralUrl,
        });
      } catch (err) {
        // User cancelled share or other error
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '30%',
          height: '100%',
          background: 'linear-gradient(to left, rgba(232, 176, 70, 0.05), transparent)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              {t('referral.yourReferralLink')}
            </Typography>
            {isAmbassador && (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 10,
                  bgcolor: 'primary.lighter',
                  color: 'primary.main',
                  fontSize: '0.625rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  border: '1px solid',
                  borderColor: 'primary.light',
                }}
              >
                {t('referral.ambassador')}
              </Box>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('referral.shareLinkDesc')}
          </Typography>
        </Box>

        {editing ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <TextField
                autoFocus
                size="small"
                value={draft}
                onChange={(e) => setDraft(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit();
                  if (e.key === 'Escape') cancelEdit();
                }}
                error={!!draftError}
                helperText={draftError || 'This changes your share link. Old links stop working.'}
                inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }}
                sx={{ flexGrow: { xs: 1, md: 0 }, minWidth: { md: 240 } }}
              />
              <Tooltip title="Save">
                <span>
                  <Button
                    variant="contained"
                    onClick={saveEdit}
                    disabled={!canSave}
                    sx={{
                      minWidth: 44,
                      width: 44,
                      height: 40,
                      borderRadius: 2.5,
                      p: 0,
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    <Check size={18} />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Cancel">
                <Button
                  variant="outlined"
                  onClick={cancelEdit}
                  disabled={updateCode.isPending}
                  sx={{
                    minWidth: 44,
                    width: 44,
                    height: 40,
                    borderRadius: 2.5,
                    p: 0,
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                  }}
                >
                  <X size={18} />
                </Button>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Box
              sx={{
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2.5,
                px: 2,
                py: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                color: 'text.primary',
                flexGrow: { xs: 1, md: 0 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: { xs: 'none', md: 300 },
              }}
            >
              {referralUrl.replace(/^https?:\/\//, '')}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={copied ? 'Copied!' : 'Copy Link'}>
                <Button
                  variant="contained"
                  onClick={handleCopy}
                  sx={{
                    minWidth: 44,
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    p: 0,
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </Button>
              </Tooltip>

              <Tooltip title="Share Link">
                <Button
                  variant="outlined"
                  onClick={handleShare}
                  sx={{
                    minWidth: 44,
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    p: 0,
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                  }}
                >
                  <Share2 size={18} />
                </Button>
              </Tooltip>

              <Tooltip title="Edit code">
                <Button
                  variant="outlined"
                  onClick={startEdit}
                  sx={{
                    minWidth: 44,
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    p: 0,
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                  }}
                >
                  <Pencil size={18} />
                </Button>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
